import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useScheduleOptions } from '~/components/schedule/lib/schedule-options';
import type { ScheduleInput } from '~/generated/graphql';
import {
  ScheduledShowForm,
  scheduledShowValidationSchema,
  useScheduledShowFormik,
  type ScheduledShow,
  type ScheduledShowFormValues,
} from './form';

export interface ScheduledShowDialogProps {
  onClose: () => void;
  onSubmit: (
    name: string,
    schedule: ScheduleInput,
    showId: number,
    scheduledShowId?: number,
  ) => Promise<void>;
  open: boolean;
  scheduledShow?: ScheduledShow;
}

export const ScheduledShowDialog = ({
  onClose,
  onSubmit,
  open,
  scheduledShow,
}: ScheduledShowDialogProps) => {
  const { t } = useTranslation();
  const scheduleOptions = useScheduleOptions({ schedule: scheduledShow?.schedule, open });

  const { isValid, serialize } = scheduleOptions;

  const handleSubmit = async (values: ScheduledShowFormValues) => {
    const schedule = serialize();
    const { showId } = scheduledShowValidationSchema.cast(values);
    await onSubmit(values.name ?? '', schedule, showId, scheduledShow?.id);
    onClose();
  };

  const initialValues = useMemo(
    () => ({
      name: scheduledShow?.name ?? '',
      showId: scheduledShow?.show.id ?? null,
    }),
    [scheduledShow],
  );

  const formik = useScheduledShowFormik(initialValues, handleSubmit);

  const handleClose = useCallback(() => {
    formik.resetForm();
    void formik.setValues(initialValues);
    onClose();
  }, [formik, initialValues, onClose]);

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={handleClose}>
      <DialogTitle onClose={handleClose}>
        {scheduledShow != null
          ? t('components.schedule.dialog.title_update')
          : t('components.schedule.dialog.title_new')}
      </DialogTitle>
      <DialogContent>
        <DialogContentTitle>
          {scheduledShow != null
            ? t('components.schedule.dialog.description_update')
            : t('components.schedule.dialog.description_new')}
        </DialogContentTitle>
        <ScheduledShowForm
          formik={formik}
          id="schedule-form"
          scheduleOptions={scheduleOptions}
          show={scheduledShow?.show ?? null}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <LoadingButton
          color="primary"
          form="schedule-form"
          variant="contained"
          disabled={!isValid || !formik.isValid}
          loading={formik.isSubmitting}
          type="submit"
        >
          {scheduledShow != null ? t('common.dialogs.update') : t('common.dialogs.create')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
